import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  downloadPayBalancesCsv,
  downloadWeeklyHoursCsv,
  fetchWeeklyHoursReport,
  type WeeklyReportResponse,
} from '../api/timeTracking';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { localCalendarDateIso } from '../utils/datetime';

const todayIso = localCalendarDateIso();

export const ReportsWeeklyHoursPage: React.FC = () => {
  const [weekStart, setWeekStart] = useState(todayIso);
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [report, setReport] = useState<WeeklyReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reportRequest = useRef(0);

  const totals = useMemo(() => {
    if (!report) {
      return { employees: 0, totalHours: 0, totalEarned: 0 };
    }
    const totalHours = report.employees.reduce((sum, employee) => sum + employee.total_hours, 0);
    const totalEarned = report.employees.reduce((sum, employee) => {
      if (employee.earned === null || employee.earned === undefined) {
        return sum;
      }
      return sum + Number(employee.earned);
    }, 0);
    return {
      employees: report.employees.length,
      totalHours: Number(totalHours.toFixed(2)),
      totalEarned: Number(totalEarned.toFixed(2)),
    };
  }, [report]);

  const loadReport = async (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }

    const requestId = ++reportRequest.current;
    try {
      setLoading(true);
      setError(null);
      const nextReport = await fetchWeeklyHoursReport(weekStart, timezone);
      if (requestId !== reportRequest.current) return;
      setReport(nextReport);
    } catch (err) {
      if (requestId !== reportRequest.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load weekly hours report');
      setReport(null);
    } finally {
      if (requestId === reportRequest.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Default week and timezone only. Later filter edits stay on Load Report.
    void loadReport();
  }, []);

  const exportWeeklyCsv = async () => {
    try {
      setExporting(true);
      setError(null);
      await downloadWeeklyHoursCsv(weekStart, timezone);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export weekly hours CSV');
    } finally {
      setExporting(false);
    }
  };

  const exportBalancesCsv = async () => {
    try {
      setExporting(true);
      setError(null);
      await downloadPayBalancesCsv();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export balances CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Reports: Weekly Employee Hours</h1>
        <p style={styles.subtitle}>
          Review total weekly hours and earned pay by employee. Lifetime owed balances are managed on Employee Management.
        </p>
      </div>

      <form style={styles.filters} onSubmit={loadReport}>
        <label style={styles.label}>
          Week Start
          <input
            type="date"
            style={styles.input}
            value={weekStart}
            onChange={(event) => setWeekStart(event.target.value)}
            required
          />
        </label>

        <label style={styles.label}>
          Timezone
          <input
            style={styles.input}
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
            placeholder="America/Los_Angeles"
            required
          />
        </label>

        <button type="submit" style={styles.primaryButton} disabled={loading}>
          {loading ? 'Loading...' : 'Load Report'}
        </button>
        <button
          type="button"
          style={styles.secondaryButton}
          disabled={exporting || loading}
          onClick={exportWeeklyCsv}
        >
          {exporting ? 'Exporting...' : 'Download Hours CSV'}
        </button>
        <button
          type="button"
          style={styles.secondaryButton}
          disabled={exporting || loading}
          onClick={exportBalancesCsv}
        >
          {exporting ? 'Exporting...' : 'Download Balances CSV'}
        </button>
      </form>

      {error && <div style={styles.error}>{error}</div>}

      {report && (
        <>
          <div style={styles.metricRow}>
            <div style={styles.metricCard}>
              <div style={styles.metricLabel}>Employees</div>
              <div style={styles.metricValue}>{totals.employees}</div>
            </div>
            <div style={styles.metricCard}>
              <div style={styles.metricLabel}>Total Hours</div>
              <div style={styles.metricValue}>{totals.totalHours}</div>
            </div>
            <div style={styles.metricCard}>
              <div style={styles.metricLabel}>Earned (week)</div>
              <div style={styles.metricValue}>${totals.totalEarned.toFixed(2)}</div>
            </div>
            <div style={styles.metricCard}>
              <div style={styles.metricLabel}>Week Window</div>
              <div style={styles.metricValueSmall}>{report.week_start} to {report.week_end}</div>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Employee</th>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Hours</th>
                  <th style={styles.th}>Rate</th>
                  <th style={styles.th}>Earned</th>
                  <th style={styles.th}>Minutes</th>
                  <th style={styles.th}>Entries</th>
                  <th style={styles.th}>Open Entries</th>
                </tr>
              </thead>
              <tbody>
                {report.employees.length === 0 && (
                  <tr>
                    <td colSpan={8} style={styles.emptyCell}>No hours recorded for this week.</td>
                  </tr>
                )}
                {report.employees.map((employee) => (
                  <tr key={employee.employee_id}>
                    <td style={styles.tdStrong}>{employee.display_name}</td>
                    <td style={styles.td}>{employee.employee_code}</td>
                    <td style={styles.td}>{employee.total_hours}</td>
                    <td style={styles.td}>
                      {employee.hourly_rate === null || employee.hourly_rate === undefined
                        ? '-'
                        : `$${Number(employee.hourly_rate).toFixed(2)}`}
                    </td>
                    <td style={styles.td}>
                      {employee.earned === null || employee.earned === undefined
                        ? '-'
                        : `$${Number(employee.earned).toFixed(2)}`}
                    </td>
                    <td style={styles.td}>{employee.total_minutes}</td>
                    <td style={styles.td}>{employee.entries_count}</td>
                    <td style={styles.td}>{employee.open_entries_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'grid',
    gap: spacing.lg,
  },
  header: {
    display: 'grid',
    gap: spacing.xs,
  },
  title: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  subtitle: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'flex-end',
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
  },
  label: {
    display: 'grid',
    gap: spacing.xs,
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
    minWidth: '220px',
  },
  input: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typographyFlat.fontSize.md,
    color: colors.text,
  },
  primaryButton: {
    border: `1px solid ${colors.primary}`,
    backgroundColor: colors.primary,
    color: colors.white,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    height: '40px',
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.surface,
    color: colors.text,
    borderRadius: borderRadius.md,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    height: '40px',
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    backgroundColor: colors.errorLight,
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.md,
    color: colors.error,
    padding: spacing.sm,
  },
  metricRow: {
    display: 'grid',
    gap: spacing.md,
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  },
  metricCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    padding: spacing.md,
    display: 'grid',
    gap: spacing.xs,
  },
  metricLabel: {
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
  },
  metricValue: {
    color: colors.text,
    fontSize: typographyFlat.fontSize.xl,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  metricValueSmall: {
    color: colors.text,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  tableWrap: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    overflowX: 'auto',
    backgroundColor: colors.surface,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typographyFlat.fontSize.sm,
  },
  tdStrong: {
    padding: `${spacing.sm} ${spacing.md}`,
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  emptyCell: {
    padding: spacing.lg,
    textAlign: 'center',
    color: colors.textSecondary,
  },
};
